import inspectPrivatePhoto from './privatePhotoImage';
// URL capabilities stay in this request's memory; components retain revocable Blob leases.
// No authorization-ticket cache: native profile selection can change without a JWT change.
const HOST = 'av4cus-synapse-pilot-assets-270254976539-use2.s3.us-east-2.amazonaws.com';
const OWNER = '270254976539';
const MAX_BYTES = 32 * 1024 * 1024;
const unavailable = () => new Error('PRIVATE_PHOTO_UNAVAILABLE');
function validateTicket(ticket, variant, watermark) {
  if (!ticket || !Number.isSafeInteger(ticket.size) || ticket.size < 1 || ticket.size > MAX_BYTES ||
      !['image/jpeg', 'image/png'].includes(ticket.contentType) || !Number.isFinite(Date.parse(ticket.expiresAt))) throw unavailable();
  const url = new URL(ticket.url);
  const raw = /^https:\/\/[^/?#]+([^?#]*)/.exec(ticket.url);
  const key = url.pathname;
  if (!raw || raw[1] !== key || /[%\\]/.test(key)) throw unavailable();
  const canonical = /^\/(production|development)\/[A-Za-z0-9_-]+\/[A-Za-z0-9_-]+\/(?:[A-Za-z0-9_-]+\/)+[A-Za-z0-9_-]+\.(jpg|png)$/.test(key);
  const thumbnail = /\/thumbnails\/(70|150|400)\/[A-Za-z0-9_-]+\.jpg$/.exec(key);
  if (watermark) {
    const scope = ticket.scope, validId = value => Number.isSafeInteger(value) && value > 0;
    if (!scope || !watermark.owner || !validId(scope.tenantId) || !validId(scope.organizationId) ||
        !validId(scope.companyId) || !validId(scope.entityId) ||
        scope.tenantId !== watermark.owner.organizationId || scope.organizationId !== watermark.owner.organizationId ||
        scope.companyId !== watermark.owner.companyId || scope.entityId !== watermark.owner.entityId ||
        scope.bucket !== 'av4cus-synapse-pilot-assets-270254976539-use2' ||
        typeof scope.prefix !== 'string' || !/^(production|development)\/[A-Za-z0-9_-]+\/[A-Za-z0-9_-]+\/(?:[A-Za-z0-9_-]+\/)+$/.test(scope.prefix) ||
        String(ticket.photoId) !== String(watermark.photoId) || ticket.version !== watermark.version ||
        ticket.contentType !== 'image/jpeg' || ticket.size > 25 * 1024 * 1024 ||
        !key.startsWith('/' + scope.prefix + 'thumbnails/watermarked/' + watermark.version + '/') ||
        !/^[A-Za-z0-9_-]+\.jpg$/.test(key.slice(('/' + scope.prefix + 'thumbnails/watermarked/' + watermark.version + '/').length))) throw unavailable();
    const ttl = Date.parse(ticket.expiresAt) - Date.parse(ticket.serverTime);
    if (!Number.isFinite(ttl) || ttl < 1 || ttl > 60000) throw unavailable();
  }
  if (url.protocol !== 'https:' || url.hostname !== HOST || url.username || url.password || url.port || url.hash ||
      !canonical || (!watermark && (variant === 'original' ? key.includes('/thumbnails/') : !thumbnail || thumbnail[1] !== variant)) ||
      url.searchParams.get('X-Amz-Expires') !== '60' || !url.searchParams.get('X-Amz-Signature') ||
      url.searchParams.get('X-Amz-SignedHeaders') !== 'host' ||
      url.searchParams.getAll('x-amz-expected-bucket-owner').length !== 1 ||
      url.searchParams.get('x-amz-expected-bucket-owner') !== OWNER) throw unavailable();
  return ticket;
}
async function boundedBlob(response, ticket) {
  const length = response.headers.get('content-length'), type = response.headers.get('content-type');
  if (!/^\d+$/.test(length || '') || Number(length) !== ticket.size || type !== ticket.contentType) throw unavailable();
  if (!response.body || !response.body.getReader) {
    const blob = await response.blob();
    if (blob.size !== ticket.size || blob.type !== ticket.contentType) throw unavailable();
    return blob;
  }
  const reader = response.body.getReader(), chunks = []; let bytes = 0, complete = false;
  try {
    for (;;) {
      const chunk = await reader.read();
      if (chunk.done) break;
      bytes += chunk.value.byteLength;
      if (bytes > ticket.size || bytes > MAX_BYTES) throw unavailable();
      chunks.push(chunk.value);
    }
    if (bytes !== ticket.size) throw unavailable();
    complete = true; return new Blob(chunks, { type: ticket.contentType });
  } finally {
    if (!complete) { try { await reader.cancel(); } catch (_) { /* original safe failure wins */ } }
    reader.releaseLock();
  }
}
export default async function downloadPrivatePhoto({ path, requestTicket, signal, isCurrent,
  watermark,
  monotonicNow = () => performance.now(),
  fetcher = (...args) => fetch(...args) }) {
  const variant = watermark ? 'watermarked' : path.split('/').pop();
  try {
    for (let attempt = 0; attempt < 2; attempt++) {
      if ((signal && signal.aborted) || !isCurrent()) throw unavailable();
      const started = watermark ? monotonicNow() : 0;
      const ticket = validateTicket(await requestTicket(), variant, watermark);
      if ((signal && signal.aborted) || !isCurrent()) throw unavailable();
      const remaining = watermark ? Date.parse(ticket.expiresAt) - Date.parse(ticket.serverTime) - (monotonicNow() - started) : Date.parse(ticket.expiresAt) - Date.now();
      if (remaining <= 2000) { if (attempt === 0) continue; throw unavailable(); }
      const response = await fetcher(ticket.url, { method: 'GET', mode: 'cors', credentials: 'omit',
        referrerPolicy: 'no-referrer', redirect: 'error', cache: 'no-store', signal });
      // Refresh once through native authorization if the capability expired in flight.
      if (response.status === 403 && attempt === 0) {
        if (response.body && response.body.cancel) { try { await response.body.cancel(); } catch (_) {} }
        continue;
      }
      if (response.status !== 200) throw unavailable();
      const blob = await boundedBlob(response, ticket);
      if ((signal && signal.aborted) || !isCurrent()) throw unavailable();
      const verified = await inspectPrivatePhoto(blob, variant);
      if ((signal && signal.aborted) || !isCurrent()) throw unavailable();
      return verified;
    }
    throw unavailable();
  } catch (_) { throw unavailable(); }
}
